import type { WagsAgentChatStatus } from "./wags-agent-chat";
import type { SeekerScreenContext } from "@wags/agent-contracts/kits-seeker-screen.contract.ts";
import type { WagsMessageMetadata } from "../wags-ui-message";
export type PendingChatSend = {
    createdAt: string;
    inputId: string;
    metadata?: WagsMessageMetadata;
    screen?: SeekerScreenContext;
    text: string;
};

export function unhydratedPendingSends<T extends PendingChatSend>(
    pendingSends: T[],
    liveInputIds: ReadonlySet<string>,
): T[] {
    return pendingSends.filter((send) => !liveInputIds.has(send.inputId));
}

export function exposedChatStatus(
    hasUnhydratedPending: boolean,
    storeStatus: WagsAgentChatStatus,
): WagsAgentChatStatus {
    return hasUnhydratedPending && storeStatus !== "streaming"
        ? "submitted"
        : storeStatus;
}

export function flushPendingChatSends<T extends PendingChatSend>({
    hydrated,
    pendingSends,
    submittingInputIds,
    failedInputIds,
    submit,
    onSubmitted,
    onFailed,
}: {
    hydrated: boolean;
    pendingSends: T[];
    submittingInputIds: Set<string>;
    failedInputIds: Set<string>;
    submit: (send: T) => Promise<void>;
    onSubmitted: (inputId: string) => void;
    onFailed: (error: Error, inputId: string) => void;
}) {
    if (!hydrated) return;

    for (const send of pendingSends) {
        if (
            submittingInputIds.has(send.inputId) ||
            failedInputIds.has(send.inputId)
        ) {
            continue;
        }
        submittingInputIds.add(send.inputId);
        void submit(send)
            .then(() => onSubmitted(send.inputId))
            .catch((error) => {
                failedInputIds.add(send.inputId);
                onFailed(
                    error instanceof Error
                        ? error
                        : new Error("Failed to submit queued input", {
                              cause: error,
                          }),
                    send.inputId,
                );
            })
            .finally(() => submittingInputIds.delete(send.inputId));
    }
}
